import { useState, useEffect } from 'react';

/**
 * usePersistentFilters
 *
 * A custom hook that persists filter state in sessionStorage.
 * - When the page refreshes (window.location.reload), filters are automatically restored.
 * - handleClearFilter() resets all filters to their initial values.
 * - handleRefresh() performs a full page reload while preserving filters.
 *
 * @param {string} key - A unique key for sessionStorage (e.g., 'resumeDashboardFilters')
 * @param {object} initialFilters - The default filter values to start with
 * @returns {{ filters, setFilters, handleClearFilter, handleRefresh }}
 */
export const usePersistentFilters = (key, initialFilters) => {
  // On first render, try to restore filters from sessionStorage
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(key);
      return saved ? JSON.parse(saved) : initialFilters;
    } catch {
      return initialFilters;
    }
  });

  // Whenever filters change, persist them to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(filters));
    } catch {
      // sessionStorage may be unavailable in some environments
    }
  }, [key, filters]);

  // Resets filters to their initial state and clears sessionStorage entry
  const handleClearFilter = () => {
    try {
      sessionStorage.removeItem(key);
    } catch {}
    setFilters(initialFilters);
  };

  // Performs a full page reload — filters are preserved because they're in sessionStorage
  const handleRefresh = () => {
    window.location.reload();
  };

  return { filters, setFilters, handleClearFilter, handleRefresh };
};
